'use client'

import cx from 'classnames'
import Image from 'next/image'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useRecoilValue } from 'recoil'
import { Autoplay, EffectFade, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperClass } from 'swiper/types'

import leftArrow from '@/assets/images/teaser/common/left-arrow.svg'
import rightArrow from '@/assets/images/teaser/common/right-arrow.svg'
import { debutSliderState } from '@/components/Page/Teaser/Introduction/Debut/InViewDebutLogic'

const FadeSlider = function ({
  items,
  speed = 1000 * 2,
  delay = 1000 * 5,
  withArrow = false,
  recoilState = false,
  rootMargin = '0px 0px 20% 0px',
  // eslint-disable-next-line no-unused-vars
  onActiveIndexChange = (swiper: SwiperClass) => {},
}: {
  items: React.ReactNode[]
  speed?: number
  delay?: number
  withArrow?: boolean
  recoilState?: boolean
  rootMargin?: string
  // eslint-disable-next-line no-unused-vars
  onActiveIndexChange?: (swiper: SwiperClass) => void
}) {
  const recoilValue = useRecoilValue(debutSliderState)

  const { ref, inView } = useInView({
    rootMargin,
    triggerOnce: true,
  })

  const swiperRef = useRef<SwiperClass>()
  const [currentIndex, setCurrentIndex] = useState(0)
  const hoge = useCallback((index: number) => {
    if (withArrow) {
      setCurrentIndex(index)
    }
  }, [])

  const [mount, setMount] = useState(false)
  const nextClass = 'swiper-arrow-right'
  const prevClass = 'swiper-arrow-left'

  useEffect(() => {
    if (recoilState && swiperRef.current) {
      swiperRef.current.slideTo(recoilValue)
    }
  }, [recoilValue])

  useEffect(() => {
    if (inView) {
      if (swiperRef.current && !mount) {
        swiperRef.current.slides[0].classList.add('swiper-slide-active')
        hoge(swiperRef.current.realIndex)
        if (!recoilState) {
          swiperRef.current.autoplay?.start()
        }
        setMount(true)
      }
    } else {
      swiperRef.current?.slides[0].classList.remove('swiper-slide-active')
      swiperRef.current?.autoplay?.stop()
    }
  }, [inView, mount])

  return (
    <div className={'relative'} ref={ref} key={inView.toString()}>
      <div className={cx({ 'mb-[20px]': withArrow })}>
        <Swiper
          modules={[Autoplay, EffectFade, Navigation]}
          slidesPerView={1}
          speed={speed}
          loop={true}
          effect={'fade'}
          autoplay={
            recoilState
              ? false
              : {
                  delay,
                  disableOnInteraction: true,
                  waitForTransition: true,
                }
          }
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          onActiveIndexChange={onActiveIndexChange}
          allowTouchMove={false}
          simulateTouch={false}
          onInit={onActiveIndexChange}
          onSlideChange={(swiper) => hoge(swiper.realIndex)}
          navigation={{ nextEl: `.${nextClass}`, prevEl: `.${prevClass}` }}
        >
          {items.map((item, index) => {
            return (
              <div key={index} className={'overflow-hidden'}>
                <SwiperSlide key={index}>{item}</SwiperSlide>
              </div>
            )
          })}
        </Swiper>
      </div>
      {withArrow && (
        <div
          className={cx(
            'flex',
            'items-center',
            'justify-center',
            'text-white',
            ...['sm:absolute', 'sm:left-1/2', 'sm:-translate-x-1/2', 'sm:bottom-10', 'sm:z-40']
          )}
        >
          <Image
            src={leftArrow.src}
            width={leftArrow.width}
            height={leftArrow.height}
            className={cx(
              'w-[39px]',
              'flex-none',
              'cursor-pointer',
              'hover:animate-[hover_.5s_ease-out_both]',
              'animate-[re-hover_.5s_ease-out_both]',
              prevClass
            )}
            alt={'Arrow Left'}
          />
          <p
            className={cx(
              'flex-none',
              'font-neue-haas-grotesk',
              'w-[12px]',
              'text-[12px]',
              'ml-[30px]',
              ...['sm:text-12px-to-vw', 'sm:ml-[40px]']
            )}
          >
            {('00' + (currentIndex + 1)).slice(-2)}
          </p>
          <hr className={cx('flex-none', 'w-[18px]', 'mx-[15px]')} />
          <p
            className={cx(
              'flex-none',
              'w-[12px]',
              'font-neue-haas-grotesk',
              'text-[12px]',
              'mr-[30px]',
              ...['sm:text-12px-to-vw', 'sm:mr-[40px]']
            )}
          >
            {('00' + items.length).slice(-2)}
          </p>
          <Image
            src={rightArrow.src}
            width={rightArrow.width}
            height={rightArrow.height}
            className={cx(
              'w-[39px]',
              'flex-none',
              'cursor-pointer',
              'hover:animate-[hover_.5s_ease-out_both]',
              'animate-[re-hover_.5s_ease-out_both]',
              nextClass
            )}
            alt={'Arrow Right'}
          />
        </div>
      )}
    </div>
  )
}

export default FadeSlider
